// Интерактивная мини-доска: игра по правилам (клик = легальный ход).
// Порт lib/board-mini.js из v3, без режима редактора (вернём при необходимости).
import { Chess, SQUARES, type Square } from 'chess.js';
import { type MoveQuality } from '../../core/classify';
import { qualityIconSvg } from '../../core/quality-icon';

const EMPTY_PLACEMENT = '8/8/8/8/8/8/8/8';

function codeFromPiece(piece: { color: string; type: string } | null | undefined): string {
  if (!piece) return '';
  return piece.color + piece.type.toUpperCase();
}

export interface MiniBoardOptions {
  onChange?: (fen: string) => void;
  /** (san, fen) — сделан легальный ход. */
  onMove?: (san: string, fen: string) => void;
}

export class MiniBoard {
  private root: HTMLElement;
  private grid!: HTMLElement;
  private chess: Chess;
  private turn: 'w' | 'b' = 'w';
  private orientation: 'white' | 'black' = 'white';
  private selected: Square | null = null;
  /** Подсветка последнего хода (from/to). */
  private lastMove: { from: string; to: string } | null = null;
  /** Стрелка-подсказка (учебные сценарии: «вот сильнейший ход»). */
  private arrow: { from: string; to: string } | null = null;
  /** Значок качества хода на клетке назначения (chess.com-стиль). */
  private marker: { square: string; quality: MoveQuality } | null = null;
  private svg!: SVGSVGElement;
  private onChange: (fen: string) => void;
  private onMove: (san: string, fen: string) => void;

  constructor(root: HTMLElement, { onChange, onMove }: MiniBoardOptions = {}) {
    this.root = root;
    this.onChange = onChange || (() => {});
    this.onMove = onMove || (() => {});
    this.chess = new Chess();
    this.chess.load(`${EMPTY_PLACEMENT} w - - 0 1`, { skipValidation: true });
    this.grid = document.createElement('div');
    this.grid.className = 'mini-board-grid';
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('viewBox', '0 0 8 8');
    this.svg.classList.add('mini-board-arrows');
    this.root.style.position = 'relative';
    this.root.replaceChildren(this.grid, this.svg);
    this.render();
  }

  /** Нарисовать/убрать стрелку (null — убрать). Не мешает кликам. */
  setArrow(arrow: { from: string; to: string } | null): void {
    this.arrow = arrow;
    this.drawArrow();
  }

  /** Значок качества хода на клетке (null — убрать). Перерисовывает доску. */
  setMoveMarker(marker: { square: string; quality: MoveQuality } | null): void {
    const a = this.marker;
    if (a?.square === marker?.square && a?.quality === marker?.quality) return;
    this.marker = marker;
    this.render();
  }

  private drawArrow(): void {
    this.svg.replaceChildren();
    if (!this.arrow) return;
    const pt = (sq: string) => {
      const f = sq.charCodeAt(0) - 97;
      const r = Number(sq[1]);
      return this.orientation === 'black' ? { x: 7 - f + 0.5, y: r - 1 + 0.5 } : { x: f + 0.5, y: 8 - r + 0.5 };
    };
    const a = pt(this.arrow.from);
    const b = pt(this.arrow.to);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    // компактная стрелка а-ля Lichess: тонкая линия, старт от края фигуры
    const sx = a.x + ux * 0.32;
    const sy = a.y + uy * 0.32;
    const head = 0.34; // длина наконечника в клетках
    const ex = b.x - ux * head;
    const ey = b.y - uy * head;
    const ns = 'http://www.w3.org/2000/svg';
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', String(sx));
    line.setAttribute('y1', String(sy));
    line.setAttribute('x2', String(ex));
    line.setAttribute('y2', String(ey));
    line.setAttribute('stroke-width', '0.15');
    line.setAttribute('stroke-linecap', 'round');
    const headEl = document.createElementNS(ns, 'polygon');
    const px = -uy; // перпендикуляр
    const py = ux;
    headEl.setAttribute(
      'points',
      `${b.x},${b.y} ${ex + px * 0.16},${ey + py * 0.16} ${ex - px * 0.16},${ey - py * 0.16}`,
    );
    this.svg.append(line, headEl);
  }

  /** Ориентация только для отображения — координаты клеток не меняются. */
  setOrientation(orientation: 'white' | 'black'): void {
    this.orientation = orientation === 'black' ? 'black' : 'white';
    this.render();
  }

  getOrientation(): 'white' | 'black' {
    return this.orientation;
  }

  /** Загрузить ПОЛНУЮ позицию из FEN (рокировки + взятие на проходе). */
  setPosition(fen: string, lastMove: { from: string; to: string } | null = null): void {
    const p = (fen || '').trim().split(/\s+/);
    this.turn = p[1] === 'b' ? 'b' : 'w';
    const full = `${p[0] || EMPTY_PLACEMENT} ${this.turn} ${p[2] || '-'} ${p[3] || '-'} ${p[4] || '0'} ${p[5] || '1'}`;
    this.chess = new Chess();
    this.chess.load(full, { skipValidation: true });
    this.selected = null;
    this.lastMove = lastMove;
    this.render();
    this.onChange(this.fen());
  }

  fen(): string {
    return this.chess.fen();
  }

  private squareClick(square: Square): void {
    const piece = this.chess.get(square);
    if (this.selected) {
      if (this.selected === square) {
        this.selected = null;
        this.render();
        return;
      }
      const legal = this.chess.moves({ square: this.selected, verbose: true }).find((m) => m.to === square);
      if (legal) {
        const made = this.chess.move({
          from: this.selected,
          to: square,
          promotion: legal.flags.includes('p') ? 'q' : undefined,
        });
        this.selected = null;
        this.turn = this.chess.turn();
        this.lastMove = made ? { from: made.from, to: made.to } : null;
        this.render();
        if (made) this.onMove(made.san, this.fen());
        return;
      }
      if (piece && piece.color === this.chess.turn()) {
        this.selected = square;
        this.render();
        return;
      }
      this.selected = null;
      this.render();
      return;
    }
    if (piece && piece.color === this.chess.turn()) {
      this.selected = square;
      this.render();
    }
  }

  render(): void {
    this.grid.replaceChildren();
    const targets = new Set<string>();
    const caps = new Set<string>();
    if (this.selected) {
      for (const m of this.chess.moves({ square: this.selected, verbose: true })) {
        targets.add(m.to);
        if (m.flags.includes('c') || m.flags.includes('e')) caps.add(m.to);
      }
    }
    // SQUARES идёт a8..h1 (белые снизу). Для чёрной ориентации — разворот.
    const order = this.orientation === 'black' ? [...SQUARES].reverse() : SQUARES;
    // Видимые края доски зависят от ориентации: координаты рисуем только на них.
    const leftFile = this.orientation === 'black' ? 7 : 0;
    const bottomRank = this.orientation === 'black' ? 8 : 1;
    for (const square of order) {
      const file = square.charCodeAt(0) - 97;
      const rank = Number(square[1]);
      const isLight = (file + rank) % 2 === 0;
      const btn = document.createElement('button');
      btn.type = 'button';
      // a1 (file 0, rank 1 → сумма нечётная) — тёмная, как на настоящей доске
      btn.className = `mini-square ${isLight ? 'light' : 'dark'}`;
      if (square === this.selected) btn.classList.add('selected');
      if (this.lastMove && (square === this.lastMove.from || square === this.lastMove.to)) btn.classList.add('last');
      if (targets.has(square)) {
        btn.classList.add('target');
        if (caps.has(square)) btn.classList.add('cap');
      }
      btn.dataset.square = square;
      const code = codeFromPiece(this.chess.get(square));
      if (code) {
        const img = document.createElement('img');
        img.alt = code;
        img.src = typeof chrome !== 'undefined' && chrome.runtime?.getURL ? chrome.runtime.getURL(`pieces/${code}.png`) : `/pieces/${code}.png`;
        btn.appendChild(img);
      }
      if (this.marker && this.marker.square === square) {
        const m = document.createElement('span');
        m.className = 'sq-marker';
        m.innerHTML = qualityIconSvg(this.marker.quality, 20);
        btn.appendChild(m);
      }
      // Координаты в стиле Lichess: ранг на левом крае, файл на нижнем; цвет = противоположная клетка.
      if (file === leftFile) {
        const c = document.createElement('span');
        c.className = `mini-coord coord-rank ${isLight ? 'on-light' : 'on-dark'}`;
        c.textContent = String(rank);
        btn.appendChild(c);
      }
      if (rank === bottomRank) {
        const c = document.createElement('span');
        c.className = `mini-coord coord-file ${isLight ? 'on-light' : 'on-dark'}`;
        c.textContent = String.fromCharCode(97 + file);
        btn.appendChild(c);
      }
      btn.addEventListener('click', () => this.squareClick(square));
      this.grid.appendChild(btn);
    }
    this.drawArrow(); // ориентация могла смениться — стрелка пересчитывается
  }
}
